import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Loader2, Lock, Infinity as InfinityIcon, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FOUNDER_LIFETIME_END = "9999-12-31T23:59:59+00:00";

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const [busy, setBusy] = useState(null); // "founders" | "monthly" | null
  const [seats, setSeats] = useState({ taken: 0, cap: 100, remaining: 100 });

  useEffect(() => {
    axios.get(`${API}/founder/availability`).then((r) => setSeats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setPolling(true);
      pollStatus(sid, 0);
    }
    if (params.get("canceled")) {
      toast.info("Checkout canceled.");
    }
  }, []); // eslint-disable-line

  const pollStatus = async (sessionId, attempt) => {
    if (attempt >= 10) {
      setPolling(false);
      toast.warning("Timed out waiting for payment confirmation. If you paid, refresh this page.");
      return;
    }
    try {
      const res = await api.get(`/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        toast.success("You're in. Welcome.");
        await refresh();
        setPolling(false);
        params.delete("session_id");
        setParams(params);
        return;
      }
      if (res.data.status === "expired") {
        toast.error("Session expired.");
        setPolling(false);
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    }
  };

  const startCheckout = async (plan) => {
    setBusy(plan);
    try {
      const res = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        plan,
      });
      window.location.href = res.data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout.";
      toast.error(typeof msg === "string" ? msg : "Could not start checkout.");
      setBusy(null);
    }
  };

  // Status interpretation
  const isAdmin = user?.is_admin;
  const isFounder = user?.is_founder || user?.subscription_ends_at === FOUNDER_LIFETIME_END;
  const subEnd = user?.subscription_ends_at && user.subscription_ends_at !== FOUNDER_LIFETIME_END
    ? new Date(user.subscription_ends_at)
    : null;
  const subActive = subEnd && subEnd > new Date();
  const trialDays = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000))
    : 0;
  const trialQuota = user?.trial_quota ?? 3;
  const trialUsed = user?.trial_used ?? 0;
  const onTrial = !isAdmin && !isFounder && !subActive && trialDays > 0;
  const expired = !isAdmin && !isFounder && !subActive && trialDays === 0;
  const isPaid = !!user?.is_paid;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-5 md:px-12 py-6 sm:py-12">
        <div className="terminal-label text-primary">[ SUBSCRIPTION ]</div>
        <h1 className="font-display text-2xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight mt-2 mb-2">Plan</h1>
        <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
          {isFounder
            ? "You're a founder. Lifetime access locked in. Thanks for backing this early."
            : isAdmin
              ? "Admin account. Full access, no billing applies."
              : subActive
                ? "You're on the monthly plan."
                : onTrial
                  ? `Free trial: ${trialUsed} of ${trialQuota} tailored applications used. Upgrade any time to open the full feed.`
                  : "Pick a plan to keep tailoring applications."
          }
        </p>

        {polling && (
          <div className="panel p-6 mb-6 flex items-center gap-4" data-testid="checkout-polling">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <div>
              <div className="terminal-label text-primary">[ CONFIRMING.PAYMENT ]</div>
              <p className="text-sm text-muted-foreground mt-1">Verifying with Stripe...</p>
            </div>
          </div>
        )}

        {/* Status banner */}
        <div className="panel p-6 mb-8" data-testid="status-card">
          <div className="terminal-label mb-3">[ STATUS ]</div>
          {isAdmin && (
            <>
              <div className="font-display text-3xl uppercase font-bold tracking-tight">ADMIN</div>
              <div className="text-xs text-muted-foreground mt-2 font-mono uppercase tracking-[0.2em]">Unlimited access</div>
            </>
          )}
          {!isAdmin && isFounder && (
            <>
              <div className="font-display text-3xl uppercase font-bold tracking-tight inline-flex items-center gap-2">
                FOUNDER <InfinityIcon className="w-7 h-7 text-primary" />
              </div>
              <div className="text-xs text-muted-foreground mt-2 font-mono uppercase tracking-[0.2em]">Lifetime - one of the first {seats.cap}</div>
            </>
          )}
          {!isAdmin && !isFounder && subActive && (
            <>
              <div className="font-display text-3xl uppercase font-bold tracking-tight" style={{ color: "hsl(var(--accent-green))" }}>ACTIVE</div>
              <div className="text-xs text-muted-foreground mt-2 font-mono uppercase tracking-[0.2em]">Renews {subEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
            </>
          )}
          {!isAdmin && !isFounder && !subActive && onTrial && (
            <>
              <div className="font-display text-3xl uppercase font-bold tracking-tight" style={{ color: "hsl(var(--accent-amber))" }}>FREE TRIAL</div>
              <div className="text-xs text-muted-foreground mt-2 font-mono uppercase tracking-[0.2em]">
                {trialUsed}/{trialQuota} applications tailored · {trialDays} day{trialDays === 1 ? "" : "s"} remaining
              </div>
            </>
          )}
          {!isAdmin && !isFounder && !subActive && expired && (
            <>
              <div className="font-display text-3xl uppercase font-bold tracking-tight text-primary">EXPIRED</div>
              <div className="text-xs text-muted-foreground mt-2 font-mono uppercase tracking-[0.2em]">Pick a plan below to keep going</div>
            </>
          )}
        </div>

        {/* Plans (hidden when admin or founder - they're already done) */}
        {!isAdmin && !isFounder && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Founders tile */}
            <div className="panel-glass corner-marks p-6 sm:p-8 relative" data-testid="plan-founders">
              <div className="text-xs mb-4 inline-flex items-center gap-2" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'hsl(var(--accent-green))' }}>
                <span className="blink-caret">→</span> LIMITED TO THE FIRST {seats.cap} USERS
              </div>
              <div className="terminal-label text-primary mb-2">[ FOUNDER ]</div>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="font-display text-5xl sm:text-6xl tracking-[-0.03em] leading-none font-bold">$200</div>
                <span className="text-[11px] tracking-[0.22em] uppercase text-foreground/55 font-mono pb-1">one-time</span>
              </div>
              <p className="font-display text-lg uppercase tracking-tight mb-5">Lifetime access. Forever.</p>
              <ul className="space-y-2 mb-6">
                {[
                  "Top-tier Sisyphean access for life",
                  "Every future feature, free",
                  "Daily AI-ranked job feed (no cap)",
                  "Pre-generated tailored resumes + cover letters",
                  "Per-application portfolio pages",
                  "Direct line to the founder",
                  "Founder badge on your profile",
                ].map((f) => (
                  <li key={f} className="text-sm flex items-start gap-2.5">
                    <Check className="w-3.5 h-3.5 text-primary mt-1 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout("founders")}
                disabled={busy !== null || seats.remaining === 0}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="plan-founders-cta"
              >
                {busy === "founders" ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : seats.remaining === 0 ? "Founder seats sold out"
                  : <>Claim founder seat <ArrowRight className="w-3.5 h-3.5" /></>
                }
              </button>
              <p className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground/60 mt-3 text-center" data-testid="founder-counter">
                {seats.taken}/{seats.cap} claimed · {seats.remaining} left
              </p>
            </div>

            {/* Monthly tile - visible but locked until founder cap is hit */}
            <div className={`brut-tile p-6 sm:p-8 relative ${seats.remaining > 0 ? "opacity-60" : ""}`} data-testid="plan-monthly">
              {seats.remaining > 0 && (
                <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2 py-1 border border-foreground/25 text-[9px] uppercase tracking-[0.22em] font-mono text-foreground/55">
                  <Lock className="w-2.5 h-2.5" /> After founders close
                </div>
              )}
              <div className="terminal-label mb-4 text-foreground/70">[ STANDARD.MONTHLY ]</div>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="font-display text-5xl sm:text-6xl tracking-[-0.03em] leading-none font-bold">$30</div>
                <span className="text-[11px] tracking-[0.22em] uppercase text-foreground/55 font-mono pb-1">/ month</span>
              </div>
              <p className="text-sm text-foreground/70 mb-5">
                {seats.remaining > 0
                  ? `Available once the ${seats.cap} founder seats are claimed. Cancel anytime.`
                  : "Cancel anytime. Pay only while you're using it."}
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  "Daily AI-ranked job feed (no cap)",
                  "Pre-generated tailored resumes + cover letters",
                  "Per-application portfolio pages",
                  "Inline editing of all materials",
                  "Sent kanban tracker",
                  "Add custom job listings",
                ].map((f) => (
                  <li key={f} className="text-sm flex items-start gap-2.5">
                    <Check className="w-3.5 h-3.5 text-foreground/60 mt-1 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startCheckout("monthly")}
                disabled={busy !== null || seats.remaining > 0}
                className="btn-secondary w-full inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                data-testid="plan-monthly-cta"
                title={seats.remaining > 0 ? "Monthly plan unlocks after the 100 founder seats are claimed" : ""}
              >
                {busy === "monthly"
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : seats.remaining > 0
                    ? <><Lock className="w-3 h-3" /> Locked until founders close</>
                    : (subActive ? "Extend +30 days" : "Subscribe")}
              </button>
              <p className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground/60 mt-3 text-center">
                Promo codes accepted at checkout
              </p>
            </div>
          </div>
        )}

        {isFounder && (
          <div className="panel p-6 mt-2 text-sm text-foreground/80" data-testid="founder-thanks">
            <div className="terminal-label text-primary mb-2">[ FOUNDER · ACCESS ]</div>
            <p className="leading-relaxed">
              You&apos;re locked in for life. Every feature we ship, you get. If you ever spot anything off, the founder bug button (bottom left) goes straight to my inbox.
            </p>
          </div>
        )}

        {isAdmin && !isFounder && (
          <p className="text-xs text-muted-foreground mt-2 font-mono uppercase tracking-[0.18em]">
            Admin access bypasses billing. To test the user flow, log in as a non-admin account.
          </p>
        )}

        {/* Removed: test-mode 4242 footer (live mode is on) */}
      </div>
    </Layout>
  );
}
